import type {
  BoxProps
} from '@chakra-ui/react'
import {
  Box,
  forwardRef,
  Grid,
  Stack,
  Text
} from '@chakra-ui/react'
import type {
  InsuranceParamsDto,
  Offer,
  OfferLeasing,
  Part
} from 'BrokerTsLib/src/dtos'
import { Insurer, Product, Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'

import { useApp } from '@/hooks/useApp'

import { useTour } from '@reactour/tour'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useGetRisks } from '../../hooks'
import { AllianzAddonGap } from '../InsuranceQuoteItem/Summary/AllianzAddonGap'
import { GapDetails } from '../InsuranceQuoteItem/Summary/GapDetails'
import { InsuranceType } from '../InsuranceQuoteItem/Summary/InsuranceType'
import { InsuranceValue } from '../InsuranceQuoteItem/Summary/InsuranceValue'
import { OfferPart } from '../InsuranceQuoteItem/Summary/OfferPart'

interface QuoteSummaryProps extends BoxProps {
  offer: OfferLeasing | Offer
}

interface PartWithChildren extends Part {
  children?: Part[]
}

export const QuoteSummary = forwardRef(
  ({ offer, ...rest }: QuoteSummaryProps, ref) => {
    const { app, product } = useApp()
    const isAllianzVehicle = offer.insurerId == Insurer.Allianz && product == Product.VehicleInsurance || offer.insurerId == Insurer.AllianzSP

    // Transformace cen, aby se správně zobrazovalo zdarma nebo N/A.
    const parts = offer.parts.map(part => {
      let price = part.price

      // nová služba Allianz nevrací ceny jednotlivých rizik, ale pouze celkovou cenu
      // nahradíme ceny za undefined aby se nezobrazovaly jako "zdarma"
      if (isAllianzVehicle && (part?.price ?? 0) <= 0)
        price = undefined

      if ((price ?? 0) < 0)
        price = undefined

      return { ...part, price }
    })

    const povPart = parts.find((x) => x.riskId === Risk.POV)
    const havPart = parts.find((x) => x.riskId === Risk.HAV)
    const glassPart = parts.find((x) => x.riskId === Risk.SKLA)
    const spolPart = parts.find((x) => x.riskId === Risk.SPOL)
    const gapPart = parts.find((x) => x.riskId == Risk.GAP)

    const mainRiskIds = [Risk.POV, Risk.HAV, Risk.SPOL, Risk.GAP]
    if (isAllianzVehicle) mainRiskIds.push(Risk.SKLA)

    const addons: PartWithChildren[] = parts
      .filter(
        (x) => !mainRiskIds.includes(x.riskId) && x.included && !x.parentId,
      )
      .map<PartWithChildren>((x) => {
        return {
          children: parts.filter(
            (p) => p.parentId && p.parentId == x.partId,
          ),
          ...x,
        }
      })

    const { data: risks } = useGetRisks()

    const { insuranceParams } = useInsuranceParams()
    const insurancePramsDto = insuranceParams as InsuranceParamsDto
    const gapVariantSelected =
      insurancePramsDto.baseParams.addonVariants?.find(
        (x) => x.riskVariantId == RiskVariant.AddonGAP,
      )?.selected ?? false

    const povText = povPart?.nameNew ?? povPart?.name
    const havText = `${havPart?.nameNew ?? havPart?.name}, ${spolPart?.nameNew ?? spolPart?.name}`
    const allianzPackageText = havPart ? havText : povText

    // pro Allianz není GAP part, je třeba ověřit, že varianta je Allrisk+GAP
    const offerVariantWithGap = (insuranceParams as InsuranceParamsDto)?.baseParams?.variants?.
      find(x => x.id == offer.insuranceVariantId && (x.variantParts?.findIndex(vp => vp.riskVariant == RiskVariant.AllriskPlusGAP) ?? -1) >= 0)
    // speciální kasko part pro Allianz obsahující GAP .. má se nabízet pouze navazující GAP
    const allianzKaskoWithGapPart = parts.findIndex(x => x.riskId == Risk.HAV && (x.description?.indexOf('Doplatek na nové (GAP)') ?? -1) > 0) != -1
    // speciální kasko part pro Allianz bez GAPu .. má se nabízet pouze nahrazující GAP
    const allianzKaskoWithoutGapPart = parts.findIndex(x => x.riskId == Risk.HAV && (x.description?.indexOf('Bez pojištění GAP') ?? -1) > 0) != -1

    const { steps } = useTour()
    const isInTourContext = steps.length > 0

    return (
      <Grid ref={ref} {...rest}>
        {!isAllianzVehicle && (
          <Grid
            sx={{
              gridTemplateColumns: { md: '1fr 1fr' },
              alignContent: 'start',
              alignItems: 'start',
              columnGap: 8,
            }}
          >
            <Box minW={0}>
              {povPart && (
                <>
                  <InsuranceType
                    label="Povinné ručení"
                    value={priceFormat(povPart.price)}
                    valid={povPart.valid}
                    errors={povPart.errors}
                  />
                  <InsuranceValue
                    value={povText!}
                    data-tour={isInTourContext && !havPart ? 'quote-item-product' : undefined}
                    tooltip={
                      product != Product.VehicleLeasingInsurance && (
                        <Stack gap={2}>
                          {povText && <Text fontWeight="bold">{povText}</Text>}
                          {povPart?.description && (
                            <Text
                              dangerouslySetInnerHTML={{
                                __html: povPart.description ?? '',
                              }}
                            ></Text>
                          )}
                        </Stack>
                      )
                    }
                  />
                </>
              )}
            </Box>

            <Box minW={0}>
              {havPart && (
                <>
                  <InsuranceType
                    label="Havarijní pojištění"
                    value={priceFormat(havPart.price)}
                    valid={havPart.valid}
                    errors={havPart.errors}
                  />
                  <InsuranceValue
                    value={havText!}
                    data-tour={isInTourContext ? 'quote-item-product' : undefined}
                    tooltip={
                      product != Product.VehicleLeasingInsurance && (
                        <Stack gap={2}>
                          {havText && <Text fontWeight="bold">{havText}</Text>}
                          {havPart?.description && (
                            <Text
                              dangerouslySetInnerHTML={{
                                __html: havPart.description ?? '',
                              }}
                            ></Text>
                          )}
                        </Stack>
                      )
                    }
                  />
                </>
              )}

              {!havPart && (
                <>
                  <InsuranceType
                    label="Havarijní pojištění"
                    value={""}
                    valid={true}
                  />
                  <InsuranceValue value={<Text color={'gray.400'}>Bez havarijního pojištění</Text>} />
                </>
              )}
            </Box>
          </Grid>
        )}

        {isAllianzVehicle && (
          <Grid
            sx={{
              gridTemplateColumns: { md: '1fr' },
              alignContent: 'start',
              alignItems: 'start',
              columnGap: 8,
            }}
          >
            <Box minW={0}>
              <InsuranceType
                label="Balíček"
                value={priceFormat(
                  (povPart?.price ?? 0) +
                  (havPart?.price ?? 0) +
                  (glassPart?.price ?? 0),
                  null,
                  '',
                )}
                valid={true}
              />
              <InsuranceValue
                value={allianzPackageText!}
                data-tour={isInTourContext ? 'quote-item-product' : undefined}
                tooltip={
                  product != Product.VehicleLeasingInsurance && (
                    <Stack gap={2}>
                      {povPart?.description && (
                        <Text
                          dangerouslySetInnerHTML={{
                            __html: povPart.description ?? '',
                          }}
                        ></Text>
                      )}
                      {havPart?.description && (
                        <Text
                          dangerouslySetInnerHTML={{
                            __html: havPart.description ?? '',
                          }}
                        ></Text>
                      )}
                      {glassPart?.description && (
                        <Text
                          dangerouslySetInnerHTML={{
                            __html: glassPart.description ?? '',
                          }}
                        ></Text>
                      )}
                    </Stack>
                  )
                }
              />
            </Box>
          </Grid>
        )}

        { }

        {(addons && addons.length > 0 || offerVariantWithGap && (allianzKaskoWithGapPart || allianzKaskoWithoutGapPart)) && (
          <Grid
            sx={{
              gridTemplateColumns: { md: '1fr' },
              alignContent: 'start',
              alignItems: 'start',
              columnGap: 8,
              pb: 4,
            }}
          >
            {gapPart && !gapVariantSelected && (
              <OfferPart key={gapPart.partId}
                {...gapPart}
                label={
                  risks?.data.find((y) => y.id == gapPart.riskId)?.name ?? ''
                }
                errors={gapPart.errors}
                price={gapPart.price}
              />
            )}

            {/* Vyber GAPU */}
            {gapPart && gapVariantSelected && <GapDetails offer={offer} />}

            {gapVariantSelected && offerVariantWithGap && (allianzKaskoWithGapPart || allianzKaskoWithoutGapPart) &&
              <AllianzAddonGap offer={offer} allowFollowingGap={allianzKaskoWithGapPart} allowReplacementGap={allianzKaskoWithoutGapPart} />}

            {addons
              .sort((x, y) => (x.name < y.name ? -1 : 1))
              .map((x) => (
                <OfferPart key={x.partId}
                  {...x}
                  label={risks?.data.find((y) => y.id == x.riskId)?.name ?? ''}
                  showPrices={
                    offer?.insurerId != Insurer.Allianz ||
                    addons.some((x) => (x.price ?? 0) > 0)
                  }
                />
              ))}
          </Grid>
        )}
      </Grid>
    )
  },
)
